<template>
    <div class="drag-and-drop-2">
        <Modal ref="modal" v-model="startModalOpen">
            <div class="info-box-modal-content checklist disable-info-icon">
                <p>
                    Und nun die nächste Aufgabe: Welches Medium wird über welchen Sinn
                    bzw. welche Sinne wahrgenommen? Ziehe die Medien in die leeren
                    Felder neben den Sinnessymbolen.
                </p>
                <p class="text-center">
                    <ControlButton active data-close>
                        Los geht’s
                    </ControlButton>
                </p>
            </div>
        </Modal>

        <Modal
            ref="modal"
            v-model="finishModalOpen"
            win-audio
        >
            <div class="info-box-modal-content checklist disable-info-icon">
                <h4 class="title text-center">
                    So stimmt es, gut gemacht!
                </h4>
                <p class="text-center">
                    <CheckIcon transition="check" />
                </p>
                <p class="text-center">
                    <ControlButton active @click="slideTo(1)">
                        Weiter
                    </ControlButton>
                </p>
            </div>
        </Modal>
        <div class="flex">
            <div>
                <transition
                    name="fade"
                    appear
                >
                    <h2 v-if="$parent.content0Visible" class="sense-header text-center">
                        Sinne
                    </h2>
                </transition>
                <SlideInTransition v-show="$parent.content1Visible" from="left">
                    <ul ref="senses" class="styleless senses-wrapper">
                        <li
                            v-for="(item, index) in senses"
                            :key="item.key"
                            class="item flex"
                            :data-key="index"
                        >
                            <div class="category">
                                <img
                                    class="block"
                                    :src="require(`@/assets/modules/2.2/sense${index + 1}.png`)"
                                >
                                <div class="label">
                                    {{ item.title }}
                                </div>
                            </div>

                            <div
                                v-for="(dropArea, dropIndex) in item.correct"
                                :key="dropArea.key"
                                ref="dropArea"
                                class="drop-area"
                                :data-key="dropIndex"
                            >
                                <div
                                    v-if="droppedItems[index][dropIndex] !== null"
                                    class="image-wrapper"
                                >
                                    <img
                                        class="block full-width"
                                        :src="require(
                                            `@/assets/modules/2.2/card${
                                                droppedItems[index][dropIndex]
                                            }.png`
                                        )"
                                    >
                                </div>
                            </div>
                        </li>
                    </ul>
                </SlideInTransition>
            </div>
            <div class="right-content">
                <transition
                    name="fade"
                    appear
                >
                    <h2 v-if="$parent.content0Visible" class="text-center">
                        Medien
                    </h2>
                </transition>
                <SlideInTransition v-show="$parent.content1Visible" from="right">
                    <ul class="styleless item-container">
                        <li
                            v-for="(item, index) in dragItems"
                            :key="item.key"
                            class="item"
                            :class="[item.disabled && 'disabled', item.incorrect && 'incorrect']"
                            :data-key="item.key"
                        >
                            <div
                                ref="items"
                                class="image-wrapper"
                                :data-key="index"
                            >
                                <img class="full-width block" :src="item.image">
                            </div>
                            <div class="label">
                                {{ item.title }}
                            </div>
                        </li>
                    </ul>
                </SlideInTransition>
            </div>
        </div>
    </div>
</template>

<script>
import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import cloneDeep from 'lodash/cloneDeep';
import SlideState from '@/mixins/SlideState';
import DirectionSlider from '@/mixins/DirectionSlider';
import config from '@/static/modules/2.2/dragItems.json';

const prepare = [];

config.media.forEach((item, index) =>
{
    item.image = require(`@/assets/modules/2.2/card${index + 1}.png`);
    item.disabled = false;
    item.incorrect = false;
});

config.senses.forEach((sense, index) =>
{
    prepare[index] = [null, null];
});

export default {
    name: 'DragAndDrop2',
    mixins: [SlideState, DirectionSlider],
    data: () => ({
        dragItems: cloneDeep(config.media),
        senses: cloneDeep(config.senses),
        droppedItems: cloneDeep(prepare),
        itemCount: 0,
        startModalOpen: false,
        finishModalOpen: false
    }),
    watch: {
        '$parent.content1Visible': function()
        {
            if (!this.getState())
            {
                this.startModalOpen = true;
            }
        }
    },
    mounted()
    {
        const {$refs} = this;

        this._baseDraggables = Draggable.create(
            [...$refs.items],
            {
                type: 'x,y',
                onDragStart: this.dragStart,
                onDragEnd: this.dragEnd
            }
        );

        const state = this.getState();

        if (state)
        {
            this.droppedItems = state;
            this.resetState();
            this.$parent.setNextButtonActive(true);
        }
    },
    destroyed()
    {
        this._baseDraggables.forEach(draggable => draggable.kill());
    },
    methods: {
        resetState()
        {
            this.droppedItems.forEach((itemArray) =>
            {
                itemArray.forEach((index) =>
                {
                    if (index === null) { return; }

                    this.dragItems[index - 1].disabled = true;
                    const draggable = Draggable.get(this.$refs.items[index - 1]);
                    this.removeDraggable(draggable);
                });
            });
        },
        dragStart(evt)
        {
            gsap.to(
                evt.target,
                {
                    scale: 0.7,
                    duration: 0.2
                }
            );

            this._currentDragElement = evt.target;
        },
        dragEnd()
        {
            const target = this._currentDragElement;
            const draggable = Draggable.get(target);
            const timeline = gsap.timeline();
            let resetPosition = true;
            let dropAreaIndex = null;
            let correctAreaHitted = false;

            this.$refs.dropArea.forEach((area, index) =>
            {
                if (draggable.hitTest(area, '70%'))
                {
                    dropAreaIndex = index;
                }
            });

            if (dropAreaIndex !== null)
            {
                const dropArea = this.$refs.dropArea[dropAreaIndex];
                const targetIndex = parseInt(target.dataset.key, 10);
                const parentKey = dropArea.parentNode.dataset.key;
                const areaState = this.droppedItems[parentKey];
                let areaKey = parseInt(dropArea.dataset.key, 10);

                correctAreaHitted = this.senses[parentKey]
                    .correct
                    .includes(targetIndex);

                const neighborKey = (areaKey) ? 0 : 1;

                if (areaState[areaKey] !== null)
                {
                    areaKey = neighborKey;
                }

                if (
                    correctAreaHitted &&
                    areaState[areaKey] === null
                )
                {
                    areaState[areaKey] = targetIndex + 1;

                    this.$set(
                        this.droppedItems,
                        dropArea.parentNode.dataset.key,
                        areaState
                    );
                    this.itemCount++;

                    this.removeDraggable(draggable, () =>
                    {
                        this.dragItems[targetIndex].disabled = true;
                    });
                    resetPosition = false;
                }
            }

            if (!correctAreaHitted && dropAreaIndex !== null)
            {
                const targetIndex = parseInt(target.dataset.key, 10);
                this.dragItems[targetIndex].incorrect = true;

                timeline.to(
                    target,
                    {duration: 0.2, rotation: 15}
                );
                timeline.to(
                    target,
                    {duration: 0.2, rotation: -15}
                );
                timeline.to(
                    target,
                    {duration: 0.2, rotation: 0}
                );
                timeline.call(() =>
                {
                    this.dragItems[targetIndex].incorrect = false;
                });
            }

            this.$audioController.play(correctAreaHitted ? 'positive' : 'negative', {volume: 0.5});

            if (resetPosition)
            {
                timeline.to(
                    target,
                    {x: 0, y: 0, duration: 0.3, scale: 1}
                );
            }

            if (this.itemCount === this.dragItems.length)
            {
                this.$parent
                    .setNextButtonActive(true, 'Weiter', () => { this.finishModalOpen = true; });
            }

            this.saveState(this.droppedItems);
            this._currentDragElement = null;
        },
        removeDraggable(draggable, onComplete = null)
        {
            const index = this._baseDraggables.findIndex(d => d === draggable);
            if (index === -1) { return; }
            this._baseDraggables.splice(index, 1);
            draggable.kill();
            const {target} = draggable;
            gsap.to(
                target,
                {
                    duration: 0.3,
                    opacity: 0,
                    onComplete
                }
            );
        }
    }
};
</script>

<style lang="scss" src="./DragAndDrop2.scss" />
